import { useAppStore } from '@/store/useAppStore'
import { useEffect, useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { pendingWriteCount, stalePendingWriteCount } from '@/lib/writeQueue'
import { SyncDiagnosticPanel } from '@/components/ui/SyncDiagnosticPanel'

export function SyncHealthIndicator() {
  const inFlight = useAppStore((s) => s._inFlight)
  const pendingSyncIds = useAppStore((s) => s._pendingSyncIds)
  const realtimeStatus = useAppStore((s) => s._realtimeStatus)
  const syncState = useAppStore((s) => s._syncState)
  const [errors, setErrors] = useState([])
  const [open, setOpen] = useState(false)
  const [queueCount, setQueueCount] = useState(0)
  const [justSynced, setJustSynced] = useState(false)
  const syncedTimerRef = useRef(null)
  const ref = useRef()

  // Poll the persistent queue. stalePendingWriteCount ignores entries <15s old so
  // normal in-flight writes don't flag as "pending". Errors are NOT auto-cleared
  // when queue drains — a permanent write failure clears its queue entry but the
  // error still deserves to be shown. User dismisses manually.
  useEffect(() => {
    const tick = () => setQueueCount(stalePendingWriteCount(15000))
    tick()
    const interval = setInterval(tick, 5000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    function onError(e) {
      const msg = e.detail?.message || 'Unknown sync error'
      const meta = e.detail?.meta || null
      const entry = { id: Date.now() + Math.random(), msg, meta, at: new Date() }
      setErrors((prev) => [entry, ...prev].slice(0, 5))
      setOpen(true)
      setQueueCount(pendingWriteCount())
    }
    window.addEventListener('prjct-write-error', onError)
    return () => window.removeEventListener('prjct-write-error', onError)
  }, [])

  // Extended 4s "Synced" flash — 2.5s was easy to miss if eyes were on a different column.
  // Successful flush also clears any transient errors so the panel doesn't keep alarming
  // about errors that have since recovered. Permanent errors + anything newer than the
  // flush are preserved (new errors keep arriving after the flush event).
  useEffect(() => {
    function onSynced() {
      setJustSynced(true)
      clearTimeout(syncedTimerRef.current)
      syncedTimerRef.current = setTimeout(() => setJustSynced(false), 4000)
      const flushedAt = Date.now()
      setErrors((prev) => prev.filter((e) => e.at.getTime() > flushedAt))
    }
    window.addEventListener('prjct-write-synced', onSynced)
    return () => {
      window.removeEventListener('prjct-write-synced', onSynced)
      clearTimeout(syncedTimerRef.current)
    }
  }, [])

  useEffect(() => {
    if (!open) return
    function onOutside(e) {
      if (!ref.current?.contains(e.target)) setOpen(false)
    }
    window.addEventListener('mousedown', onOutside)
    return () => window.removeEventListener('mousedown', onOutside)
  }, [open])

  const hasPending = pendingSyncIds.length > 0 || queueCount > 0
  const pendingCount = Math.max(pendingSyncIds.length, queueCount)
  const hasError = errors.length > 0 && queueCount === 0
  const rtDegraded = realtimeStatus === 'degraded' || realtimeStatus === 'offline'

  // Priority order: error → stuck → pending → saving → rtDegraded → healthy.
  // 'stuck' (auth-lock wedge) outranks 'pending' so a wedged queue can't masquerade as a
  // benign "pending" just because the websocket is still joined.
  const isStuck = syncState === 'stuck-reconnecting'
  let state
  if (hasError) state = 'error'
  else if (isStuck) state = 'stuck'
  else if (hasPending) state = 'pending'
  else if (inFlight > 0) state = 'saving'
  else if (rtDegraded) state = 'rt-degraded'
  else state = 'healthy'

  const offlineQueued = syncState === 'offline-queued'

  // Dot styling per state
  const dotConfig = {
    saving: { cls: 'w-2 h-2 bg-orange-400 animate-pulse shadow-[0_0_6px_rgba(251,146,60,0.5)]', label: 'Saving', labelCls: 'text-orange-400', title: 'Saving changes…' },
    stuck: { cls: 'w-2 h-2 bg-red-500 animate-pulse shadow-[0_0_6px_rgba(239,68,68,0.5)]', label: 'Sync stuck', labelCls: 'text-red-400', title: 'Sync stuck — recovering the connection. Click for details.' },
    pending: { cls: 'w-1.5 h-1.5 bg-yellow-400 animate-pulse', label: offlineQueued ? `${pendingCount} queued` : `${pendingCount} pending`, labelCls: 'text-yellow-400', title: offlineQueued ? `${pendingCount} change${pendingCount !== 1 ? 's' : ''} queued — offline, will sync when reconnected` : `${pendingCount} change${pendingCount !== 1 ? 's' : ''} pending sync — click for details` },
    error: { cls: 'w-1.5 h-1.5 bg-red-500 animate-pulse', label: 'Sync error', labelCls: 'text-red-400', title: 'Sync error — click for details' },
    'rt-degraded': { cls: 'w-1.5 h-1.5 bg-orange-400 animate-pulse', label: 'Reconnecting', labelCls: 'text-orange-400', title: 'Realtime connection degraded — click for details' },
    healthy: justSynced
      ? { cls: 'w-2 h-2 bg-green-400 shadow-[0_0_6px_rgba(74,222,128,0.45)]', label: 'Synced', labelCls: 'text-green-400', title: 'All changes synced' }
      : { cls: 'w-1.5 h-1.5 bg-green-500/60', label: null, labelCls: '', title: 'All changes saved — click for diagnostics' },
  }
  const cfg = dotConfig[state]

  const interactive = state !== 'saving'

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => interactive && setOpen((v) => !v)}
        disabled={!interactive}
        className="flex items-center gap-1.5 transition-opacity active:opacity-70 disabled:cursor-default disabled:opacity-100"
        title={cfg.title}
      >
        <span className={`rounded-full flex-shrink-0 transition-all duration-300 ${cfg.cls}`} />
        <AnimatePresence initial={false} mode="wait">
          {cfg.label && (
            <motion.span
              key={cfg.label}
              initial={{ opacity: 0, x: -3 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -3 }}
              transition={{ duration: 0.15, ease: [0.23, 1, 0.32, 1] }}
              className={`hidden lg:block text-[10px] font-medium ${cfg.labelCls}`}
            >
              {cfg.label}
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -4 }}
            transition={{ duration: 0.15, ease: [0.23, 1, 0.32, 1] }}
            style={{ transformOrigin: 'top right' }}
            className="absolute right-0 top-full mt-2 z-50"
          >
            <SyncDiagnosticPanel
              errors={errors}
              onDismissError={(id) => setErrors((prev) => prev.filter((e) => e.id !== id))}
              onClearErrors={() => setErrors([])}
              onClose={() => setOpen(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
